import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import SignatureInput from '../components/SignatureInput';
import WoundHealingTracker from '../components/WoundHealingTracker';
import { generateWoundCarePDF, downloadPDF, printPDF } from '../utils/pdfExport';
import { cachePdfPatients } from '../utils/fillable';
import { FileText, Save, ArrowLeft, Download, Printer, Plus, Trash2, Camera, Image, X, Upload, ZoomIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WoundCareForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('formId');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    patientName: '',
    medicalRecordNumber: '',
    woundImages: [], // Array of wound image objects {id, imageData, dateTaken, description}
    entries: [],
    nurseSignature: { typed: '', audioURL: null, canvasDataURL: null },
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const woundImageInputRef = useRef(null);

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients); cachePdfPatients(data.patients);
        if (patientId) {
          const patient = data.patients.find(p => p.id === patientId);
          if (patient) setFormData(prev => ({ ...prev, patientName: `${patient.firstName} ${patient.lastName}` }));
        }
      }
    } catch (error) { console.error('Error:', error); }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/wound-care/${formId}`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) setFormData(data.form);
    } catch (error) { toast.error('Failed to load form'); }
    finally { setLoading(false); }
  };

  const handlePatientChange = (e) => {
    const patient = patients.find(p => p.id === e.target.value);
    if (patient) setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: `${patient.firstName} ${patient.lastName}` }));
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const addEntry = () => {
    setFormData(prev => ({
      ...prev,
      entries: [...prev.entries, {
        date: new Date().toISOString().split('T')[0],
        woundLocation: '',
        measurements: '',
        drainageAmount: '',
        drainageColor: '',
        drainageOdor: '',
        comments: ''
      }]
    }));
  };

  const updateEntry = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map((entry, i) => i === index ? { ...entry, [field]: value } : entry)
    }));
  };

  const removeEntry = (index) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.patientId) { toast.error('Please select a patient'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId ? `${API_URL}/api/forms/wound-care/${formId}` : `${API_URL}/api/forms/wound-care`;
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form) navigate(`/dashboard/forms/wound-care?formId=${data.form.id}`);
      }
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard/documentation')} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#f39c12]" />Daily Wound Care Flow Sheet
                </h1>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  Track wound assessments over time
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                const doc = generateWoundCarePDF(formData);
                downloadPDF(doc, `wound-care-${formData.patientName || 'form'}.pdf`);
              }} data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-2" />PDF</Button>
              <Button variant="outline" onClick={() => {
                const doc = generateWoundCarePDF(formData);
                printPDF(doc);
              }} data-testid="print-pdf-btn"><Printer className="w-4 h-4 mr-2" />Print</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Patient Info */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Patient *</Label>
              <select value={formData.patientId} onChange={handlePatientChange}
                className="w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#f39c12]">
                <option value="">Select patient</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
              </select>
            </div>
            <div>
              <Label>Medical Record #</Label>
              <Input value={formData.medicalRecordNumber} onChange={(e) => handleChange('medicalRecordNumber', e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>

        {/* Wound Images Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#f39c12]" />
              <h2 className="text-lg font-semibold">Wound Images</h2>
              <span className="text-xs text-gray-500">(Click image to enlarge)</span>
            </div>
            <Button 
              onClick={() => woundImageInputRef.current?.click()}
              className="bg-[#f39c12] hover:bg-[#e67e22] text-white"
              disabled={uploading}
              data-testid="upload-wound-image-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Wound Image'}
            </Button>
            <input
              type="file"
              ref={woundImageInputRef}
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                if (!file.type.startsWith('image/')) {
                  toast.error('Please select an image file');
                  return;
                }
                if (file.size > 10 * 1024 * 1024) {
                  toast.error('Image size must be less than 10MB');
                  return;
                }
                setUploading(true);
                const reader = new FileReader();
                reader.onloadend = () => {
                  const newImage = {
                    id: Date.now().toString(),
                    imageData: reader.result,
                    dateTaken: new Date().toISOString().split('T')[0],
                    description: ''
                  };
                  setFormData(prev => ({
                    ...prev,
                    woundImages: [...(prev.woundImages || []), newImage]
                  }));
                  toast.success('Wound image added');
                  setUploading(false);
                };
                reader.readAsDataURL(file);
                e.target.value = '';
              }}
              accept="image/*"
              className="hidden"
              data-testid="wound-image-input"
            />
          </div>

          {/* Image Grid */}
          {(!formData.woundImages || formData.woundImages.length === 0) ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Image className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No wound images uploaded yet</p>
              <p className="text-sm text-gray-400 mt-1">Upload images to document wound progression</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {formData.woundImages.map((image, index) => (
                <div key={image.id} className="relative group border rounded-lg overflow-hidden bg-gray-50">
                  <img 
                    src={image.imageData} 
                    alt={`Wound ${index + 1}`}
                    className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedImage(image)}
                    data-testid={`wound-image-${index}`}
                  />
                  <div className="p-2">
                    <input
                      type="date"
                      value={image.dateTaken}
                      onChange={(e) => {
                        const updated = formData.woundImages.map(img => 
                          img.id === image.id ? {...img, dateTaken: e.target.value} : img
                        );
                        setFormData(prev => ({...prev, woundImages: updated}));
                      }}
                      className="w-full text-xs border rounded px-2 py-1 mb-1"
                    />
                    <input
                      type="text"
                      value={image.description}
                      onChange={(e) => {
                        const updated = formData.woundImages.map(img => 
                          img.id === image.id ? {...img, description: e.target.value} : img
                        );
                        setFormData(prev => ({...prev, woundImages: updated}));
                      }}
                      placeholder="Description..."
                      className="w-full text-xs border rounded px-2 py-1"
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData(prev => ({
                        ...prev,
                        woundImages: prev.woundImages.filter(img => img.id !== image.id)
                      }));
                      toast.success('Image removed');
                    }}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-wound-image-${index}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => setSelectedImage(image)}
                    className="absolute top-1 left-1 p-1 bg-white/80 text-gray-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ZoomIn className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Wound Healing Progress Tracker */}
        {formData.woundImages && formData.woundImages.length >= 2 && (
          <div className="mb-6">
            <WoundHealingTracker 
              woundImages={formData.woundImages}
              woundMeasurements={formData.entries?.map(entry => ({
                date: entry.date,
                width: parseFloat(entry.measurements?.split('x')?.[0]) || 0,
                length: parseFloat(entry.measurements?.split('x')?.[1]) || 0
              })).filter(m => m.width > 0 || m.length > 0)}
            />
          </div>
        )}

        {/* Image Lightbox Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="wound-image-lightbox"
          >
            <div className="relative max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
              <img 
                src={selectedImage.imageData} 
                alt="Wound detail"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="mt-2 text-white text-center">
                <p className="text-sm">Date: {selectedImage.dateTaken}</p>
                {selectedImage.description && <p className="text-sm">Description: {selectedImage.description}</p>}
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-2 -right-2 p-2 bg-white text-gray-800 rounded-full shadow-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Wound Care Entries Table */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Wound Care Entries</h2>
            <Button onClick={addEntry} className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="w-4 h-4 mr-2" />Add Entry
            </Button>
          </div>

          {formData.entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No wound care entries yet</p>
              <p className="text-sm">Click &quot;Add Entry&quot; to record a wound assessment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border p-2 text-left text-sm font-medium">Date</th>
                    <th className="border p-2 text-left text-sm font-medium">Wound Location</th>
                    <th className="border p-2 text-left text-sm font-medium">Measurements</th>
                    <th className="border p-2 text-left text-sm font-medium">Drainage Amt</th>
                    <th className="border p-2 text-left text-sm font-medium">Color</th>
                    <th className="border p-2 text-left text-sm font-medium">Odor</th>
                    <th className="border p-2 text-left text-sm font-medium">Comments</th>
                    <th className="border p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.entries.map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border p-1">
                        <Input type="date" value={entry.date} onChange={(e) => updateEntry(index, 'date', e.target.value)} className="text-sm" />
                      </td>
                      <td className="border p-1">
                        <Input value={entry.woundLocation} onChange={(e) => updateEntry(index, 'woundLocation', e.target.value)} 
                          className="text-sm" placeholder="e.g., Left heel" />
                      </td>
                      <td className="border p-1">
                        <Input value={entry.measurements} onChange={(e) => updateEntry(index, 'measurements', e.target.value)} 
                          className="text-sm" placeholder="L x W x D" />
                      </td>
                      <td className="border p-1">
                        <Input value={entry.drainageAmount} onChange={(e) => updateEntry(index, 'drainageAmount', e.target.value)} 
                          className="text-sm" placeholder="None/Min/Mod/Max" />
                      </td>
                      <td className="border p-1">
                        <Input value={entry.drainageColor} onChange={(e) => updateEntry(index, 'drainageColor', e.target.value)} 
                          className="text-sm" placeholder="Clear/Yellow/Red" />
                      </td>
                      <td className="border p-1">
                        <Input value={entry.drainageOdor} onChange={(e) => updateEntry(index, 'drainageOdor', e.target.value)} 
                          className="text-sm" placeholder="None/Foul" />
                      </td>
                      <td className="border p-1">
                        <Input value={entry.comments} onChange={(e) => updateEntry(index, 'comments', e.target.value)} 
                          className="text-sm" placeholder="Notes" />
                      </td>
                      <td className="border p-1 text-center">
                        <Button variant="ghost" size="sm" onClick={() => removeEntry(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Signature - Comprehensive with Type/Voice Recording/Handwritten */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Signature</h2>
          <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SignatureInput 
              label="Nurse Signature" 
              value={formData.nurseSignature} 
              onChange={(value) => handleChange('nurseSignature', value)} 
            />
            <div>
              <VoiceDictationInput label="Date" value={formData.signatureDate} onChange={(e) => handleChange('signatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WoundCareForm;
